import { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, Toast, MultiSelectDropdown, ConfirmModal } from '../../components';
import type { ToastType } from '../../components/Toast';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useDeleteRiskMutation } from '../../hooks/queries';
import { risksApi, goalsApi, organizationalUnitsApi } from '../../services/apiService';
import { StrategicGoalList, RiskType, OrganizationalUnitList } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { unitTreeOptions } from '../../utils/unitTree';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
type NavigationProp = NativeStackNavigationProp<any>;
type RouteParams = {
  AddRisk: {
    riskId?: number;
    relatedType?: string;
    relatedId?: number;
  };
};

// Icons
const ChevronDownIcon = ({ size = 14, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Risk type options - labels will be translated in the component
const RISK_TYPE_OPTIONS: { value: RiskType; labelKey: string }[] = [
  { value: 'strategic', labelKey: 'risks.typeStrategic' },
  { value: 'operational', labelKey: 'risks.typeOperational' },
  { value: 'financial', labelKey: 'risks.typeFinancial' },
  { value: 'compliance', labelKey: 'risks.typeCompliance' },
];

// Risk level options - labels will be translated in the component
const RISK_LEVEL_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'low', labelKey: 'risks.levelLow' },
  { value: 'medium', labelKey: 'risks.levelMedium' },
  { value: 'high', labelKey: 'risks.levelHigh' },
  { value: 'critical', labelKey: 'risks.levelCritical' },
];

export default function AddRiskScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProp<RouteParams, 'AddRisk'>>();
  const { riskId, relatedType, relatedId } = route.params || {};
  const isEditMode = !!riskId;

  // Debug logging
  console.log('AddRiskScreen - route.params:', route.params);
  console.log('AddRiskScreen - riskId:', riskId, 'type:', typeof riskId);
  console.log('AddRiskScreen - isEditMode:', isEditMode);
  const { isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    dropdownTextSelected: colors.textPrimary,
    dropdownMenu: colors.card,
    dropdownMenuBorder: colors.border,
    dropdownItem: colors.textPrimary,
    dropdownItemBorder: isDark ? colors.border : tokens.colors.raw.gray["100"],
    matrixCell: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    matrixCellBorder: colors.border,
    loadingText: colors.textMuted,
  };

  // Redirect view-only users (executive) - they cannot add/edit
  useEffect(() => {
    if (isViewOnly) {
      console.log('🚫 AddRisk: View-only user - redirecting');
      navigation.goBack();
    }
  }, [isViewOnly, navigation]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteRiskMutation = useDeleteRiskMutation();
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);

  // Form state
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [selectedGoal, setSelectedGoal] = useState<number | null>(relatedId || null);
  const [riskType, setRiskType] = useState<RiskType | ''>('');
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [probability, setProbability] = useState<number>(3);
  const [impact, setImpact] = useState<number>(3);
  const [currentProcedures, setCurrentProcedures] = useState('');
  const [mitigationActions, setMitigationActions] = useState('');
  const [expectedImpact, setExpectedImpact] = useState('');
  const [riskOwner, setRiskOwner] = useState('');
  const [units, setUnits] = useState<{ value: string; label: string; depth: number }[]>([]);
  const [responsibleUnits, setResponsibleUnits] = useState<string[]>([]);

  // Dropdown states
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);

  // Toast state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<ToastType>('error');

  const showToast = (message: string, type: ToastType = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  // Field error states
  const [errors, setErrors] = useState<Record<string, string>>({});

  useUnsavedChangesWarning(!!name || !!currentProcedures || !!mitigationActions);

  useEffect(() => {
    const fetchData = async () => {
      const currentRiskId = route.params?.riskId;
      setLoading(true);
      console.log('=== fetchData START ===');
      console.log('route.params:', JSON.stringify(route.params));
      console.log('currentRiskId:', currentRiskId, 'type:', typeof currentRiskId);

      try {
        // Fetch goals for dropdown
        console.log('Fetching goals...');
        const goalsData = await goalsApi.list({ page_size: 100 });
        console.log('Goals fetched:', goalsData.results?.length || 0, 'goals');
        setGoals(goalsData.results || []);

        // Fetch organizational units for dropdown
        const unitsData = await organizationalUnitsApi.list({ page_size: 9999 });
        // Every department and section, each followed by its sub-units.
        setUnits(unitTreeOptions(unitsData.results || [], (u: OrganizationalUnitList) => translate(u.name_ar || u.name)));

        // If editing, fetch risk data
        if (currentRiskId) {
          console.log('Fetching risk data for id:', currentRiskId);
          const riskData = await risksApi.get(Number(currentRiskId));
          console.log('=== Risk data fetched ===');
          console.log('riskData:', JSON.stringify(riskData, null, 2));

          // Set all form fields
          setName(riskData.name || '');
          setCode(riskData.code || '');
          setSelectedGoal(riskData.related_id || null);
          setRiskType(riskData.risk_type || '');
          setSelectedLevel(riskData.risk_level || '');
          setProbability(riskData.probability || 3);
          setImpact(riskData.impact || 3);
          setCurrentProcedures(riskData.internal_causes || '');
          setMitigationActions(riskData.mitigation_actions || '');
          setExpectedImpact(riskData.expected_impact || '');
          setRiskOwner(riskData.risk_owner || '');
          setResponsibleUnits((riskData.responsible_units || []).map((id: number) => id.toString()));

          console.log('Form fields set successfully');
        } else {
          console.log('No riskId - creating new risk mode');
        }
      } catch (err: any) {
        console.error('Failed to fetch data:', err);
        console.error('Error details:', err?.response?.data);
        showToast(t('common.loadingError'), 'error');
      } finally {
        setLoading(false);
        console.log('=== fetchData END ===');
      }
    };

    fetchData();
  }, [route.params?.riskId]);

  const clearFieldError = (field: string) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSave = async () => {
    // Validate all fields at once
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('risks.enterRiskName');
    }
    if (!selectedGoal) {
      newErrors.selectedGoal = t('risks.selectStrategicGoal');
    }
    if (!riskType) {
      newErrors.riskType = t('risks.selectRiskType');
    }
    if (!selectedLevel) {
      newErrors.selectedLevel = t('risks.selectRiskLevel');
    }
    if (!riskOwner.trim()) {
      newErrors.riskOwner = t('risks.enterRiskOwner');
    }
    if (!expectedImpact.trim()) {
      newErrors.expectedImpact = t('risks.enterExpectedImpact');
    }
    if (!currentProcedures.trim()) {
      newErrors.currentProcedures = t('risks.enterCurrentProcedures');
    }
    if (!mitigationActions.trim()) {
      newErrors.mitigationActions = t('risks.enterMitigationActions');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        code: code.trim(),
        related_type: relatedType || 'strategic_goal',
        related_id: selectedGoal,
        risk_type: riskType,
        probability,
        impact,
        internal_causes: currentProcedures.trim(),
        mitigation_actions: mitigationActions.trim(),
        expected_impact: expectedImpact.trim(),
        risk_owner: riskOwner.trim(),
        responsible_units: responsibleUnits.map(Number),
      };

      console.log('=== SAVING RISK ===');
      console.log('Data:', JSON.stringify(data, null, 2));

      let successMessage = '';

      if (isEditMode && riskId) {
        const result = await risksApi.update(riskId, data);
        console.log('Update result:', result);
        successMessage = t('risks.updateSuccess');
      } else {
        const result = await risksApi.create(data);
        console.log('Create result:', result);
        successMessage = t('risks.createSuccess');
      }

      // Navigate to Risks screen with success message
      navigation.navigate('Risks', { successMessage });
    } catch (err: any) {
      console.error('=== SAVE ERROR ===');
      console.error('Error object:', err);
      console.error('Error message:', err?.message);
      console.error('Error response status:', err?.response?.status);
      console.error('Error response data:', JSON.stringify(err?.response?.data, null, 2));

      // Handle different error types
      let errorMessage = t('risks.saveFailed');

      if (err?.message === 'Network Error') {
        errorMessage = t('messages.error.networkError');
      } else if (err?.response?.status === 401) {
        errorMessage = t('messages.error.sessionExpired');
      } else if (err?.response?.status === 403) {
        errorMessage = t('risks.noPermission');
      } else if (err?.response?.status === 400) {
        // Validation errors
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors) {
          errorMessage = data.non_field_errors.join('\n');
        } else {
          // Show field-specific errors
          const errors = Object.entries(data)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMessage = errors || t('risks.saveFailed');
        }
      } else if (err?.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else {
          errorMessage = t('messages.error.unexpected', { status: err?.response?.status || t('common.unknown') });
        }
      }

      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const handleDelete = async () => {
    if (!riskId) return;
    setShowDeleteConfirm(false);
    try {
      const response = await deleteRiskMutation.mutateAsync(riskId);
      // 202 = org_employee delete request sent for approval, 204 = deleted
      const isPending = response?.status === 202 || response?.data?.status === 'pending';
      navigation.navigate('Risks', {
        successMessage: isPending ? t('messages.deleteRequestSent') : t('risks.deleteSuccess'),
      });
    } catch (err: any) {
      console.error('Risk delete error:', err);
      const data = err?.response?.data;
      showToast(data?.detail || data?.message || t('risks.deleteFailed'), 'error');
    }
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: {
      name,
      code,
      selectedGoal,
      riskType,
      selectedLevel,
      probability,
      impact,
      currentProcedures,
      mitigationActions,
      expectedImpact,
      riskOwner,
      responsibleUnits,
    },
    ready: !loading,
    onExit: handleCancel,
  });

  const closeAllDropdowns = () => {
    setShowGoalDropdown(false);
    setShowTypeDropdown(false);
    setShowLevelDropdown(false);
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={styles.loadingText}>{t('common.loading')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onHide={() => setToastVisible(false)}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('navigation.risks'), route: 'Risks' },
            { label: isEditMode ? t('risks.editRisk') : t('risks.addRisk') },
          ]}
        />

        {/* Title */}
        <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('risks.editRisk') : t('risks.addRisk')}</Text>

        {/* Form Card */}
        <View style={[styles.formCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Section Title */}
          <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>

          {/* Row 1: Risk Name + Code */}
          <View style={[styles.formRow, { zIndex: 30 }]}>
            {/* Code */}
            <View style={[styles.formGroup, styles.formGroupHalf]}>
              <Text style={styles.label}>
                {t('risks.riskCode')}
                <Text style={styles.required}> *</Text>
              </Text>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={code}
                onChangeText={(val) => { setCode(val); clearFieldError('code'); }}
                placeholder={t('risks.riskCodePlaceholder')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            {/* Risk Name */}
            <View style={[styles.formGroup, styles.formGroupHalf]}>
              <Text style={styles.label}>
                {t('risks.riskName')}
                <Text style={styles.required}> *</Text>
              </Text>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: errors.name ? tokens.colors.raw.red["500"] : themeColors.inputBorder, color: themeColors.inputText }]}
                value={name}
                onChangeText={(val) => { setName(val); clearFieldError('name'); }}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
              {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
            </View>
          </View>

          {/* Row 2: Strategic Goals (full width) */}
          <View style={[styles.formGroup, { zIndex: 20 }]}>
            <Text style={styles.label}>
              {t('risks.strategicGoals')}
              <Text style={styles.required}> *</Text>
            </Text>
            <TouchableOpacity
              style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: errors.selectedGoal ? tokens.colors.raw.red["500"] : themeColors.dropdownBorder }]}
              onPress={() => {
                closeAllDropdowns();
                setShowGoalDropdown(!showGoalDropdown);
              }}
            >
              <ChevronDownIcon size={14} color={themeColors.iconColor} />
              <Text style={[styles.dropdownText, selectedGoal ? { color: themeColors.dropdownTextSelected } : { color: themeColors.dropdownText }]} numberOfLines={1}>
                {selectedGoal
                  ? translate(goals.find(g => g.id === selectedGoal)?.title) || t('risks.selectStrategicGoals')
                  : t('risks.selectStrategicGoals')}
              </Text>
            </TouchableOpacity>
            {errors.selectedGoal && <Text style={styles.errorText}>{errors.selectedGoal}</Text>}
            {showGoalDropdown && (
              <View style={[styles.dropdownMenu, { backgroundColor: themeColors.dropdownMenu, borderColor: themeColors.dropdownMenuBorder }]}>
                <ScrollView style={styles.dropdownScroll} nestedScrollEnabled>
                  {goals.length === 0 && (
                    <View style={[styles.dropdownItem, { backgroundColor: themeColors.dropdownMenu, borderBottomColor: themeColors.dropdownItemBorder }]}>
                      <Text style={[styles.dropdownItemText, { color: themeColors.dropdownText }]}>{t('common.noData')}</Text>
                    </View>
                  )}
                  {goals.map((goal) => (
                    <TouchableOpacity
                      key={goal.id}
                      style={[styles.dropdownItem, { backgroundColor: themeColors.dropdownMenu, borderBottomColor: themeColors.dropdownItemBorder }]}
                      onPress={() => {
                        setSelectedGoal(goal.id);
                        clearFieldError('selectedGoal');
                        setShowGoalDropdown(false);
                      }}
                    >
                      <Text style={[
                        styles.dropdownItemText,
                        { color: themeColors.dropdownItem },
                        selectedGoal === goal.id && styles.dropdownItemTextActive
                      ]} numberOfLines={2}>
                        {translate(goal.title)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>

          {/* Implementation Responsibility (units multi-select) */}
          <View style={[styles.formGroup, { zIndex: 15 }]}>
            <Text style={[styles.label, { marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
            <MultiSelectDropdown
              values={responsibleUnits}
              options={units}
              placeholder={t('initiatives.selectResponsibleUnits')}
              onChange={setResponsibleUnits}
            />
          </View>

          {/* Row 3: Risk Type + Risk Level */}
          <View style={[styles.formRow, { zIndex: 10 }]}>
            {/* Risk Level */}
            <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: 11 }]}>
              <Text style={styles.label}>
                {t('risks.riskLevel')}
                <Text style={styles.required}> *</Text>
              </Text>
              <TouchableOpacity
                style={[styles.dropdown, { borderColor: errors.selectedLevel ? tokens.colors.raw.red["500"] : themeColors.dropdownBorder }]}
                onPress={() => {
                  closeAllDropdowns();
                  setShowLevelDropdown(!showLevelDropdown);
                }}
              >
                <ChevronDownIcon size={14} color={themeColors.iconColor} />
                <Text style={[styles.dropdownText, selectedLevel ? { color: themeColors.dropdownTextSelected } : {}]}>
                  {selectedLevel
                    ? t(RISK_LEVEL_OPTIONS.find(l => l.value === selectedLevel)?.labelKey || '')
                    : t('risks.selectRiskLevel')}
                </Text>
              </TouchableOpacity>
              {errors.selectedLevel && <Text style={styles.errorText}>{errors.selectedLevel}</Text>}
              {showLevelDropdown && (
                <View style={styles.dropdownMenu}>
                  <ScrollView style={styles.dropdownScroll} nestedScrollEnabled>
                    {RISK_LEVEL_OPTIONS.map((level) => (
                      <TouchableOpacity
                        key={level.value}
                        style={styles.dropdownItem}
                        onPress={() => {
                          setSelectedLevel(level.value);
                          clearFieldError('selectedLevel');
                          switch (level.value) {
                            case 'low':
                              setProbability(2);
                              setImpact(2);
                              break;
                            case 'medium':
                              setProbability(3);
                              setImpact(3);
                              break;
                            case 'high':
                              setProbability(3);
                              setImpact(5);
                              break;
                            case 'critical':
                              setProbability(5);
                              setImpact(5);
                              break;
                          }
                          setShowLevelDropdown(false);
                        }}
                      >
                        <Text style={[
                          styles.dropdownItemText,
                          selectedLevel === level.value && styles.dropdownItemTextActive
                        ]}>
                          {t(level.labelKey)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}
            </View>

            {/* Risk Type */}
            <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: 12 }]}>
              <Text style={styles.label}>
                {t('risks.riskType')}
                <Text style={styles.required}> *</Text>
              </Text>
              <TouchableOpacity
                style={[styles.dropdown, { borderColor: errors.riskType ? tokens.colors.raw.red["500"] : themeColors.dropdownBorder }]}
                onPress={() => {
                  closeAllDropdowns();
                  setShowTypeDropdown(!showTypeDropdown);
                }}
              >
                <ChevronDownIcon size={14} color={themeColors.iconColor} />
                <Text style={[styles.dropdownText, riskType ? { color: themeColors.dropdownTextSelected } : {}]}>
                  {riskType
                    ? t(RISK_TYPE_OPTIONS.find(typeOpt => typeOpt.value === riskType)?.labelKey || '')
                    : t('risks.selectRiskType')}
                </Text>
              </TouchableOpacity>
              {errors.riskType && <Text style={styles.errorText}>{errors.riskType}</Text>}
              {showTypeDropdown && (
                <View style={styles.dropdownMenu}>
                  <ScrollView style={styles.dropdownScroll} nestedScrollEnabled>
                    {RISK_TYPE_OPTIONS.map((typeOpt) => (
                      <TouchableOpacity
                        key={typeOpt.value}
                        style={styles.dropdownItem}
                        onPress={() => {
                          setRiskType(typeOpt.value);
                          clearFieldError('riskType');
                          setShowTypeDropdown(false);
                        }}
                      >
                        <Text style={[
                          styles.dropdownItemText,
                          riskType === typeOpt.value && styles.dropdownItemTextActive
                        ]}>
                          {t(typeOpt.labelKey)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}
            </View>
          </View>

          {/* Row 4: Risk Owner + Expected Impact */}
          <View style={styles.formRow}>
            {/* Expected Impact Description */}
            <View style={[styles.formGroup, styles.formGroupHalf]}>
              <Text style={styles.label}>
                {t('risks.expectedImpact')}
                <Text style={styles.required}> *</Text>
              </Text>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: errors.expectedImpact ? tokens.colors.raw.red["500"] : themeColors.inputBorder, color: themeColors.inputText }]}
                value={expectedImpact}
                onChangeText={(val) => { setExpectedImpact(val); clearFieldError('expectedImpact'); }}
                placeholder={t('risks.expectedImpactPlaceholder')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
              {errors.expectedImpact && <Text style={styles.errorText}>{errors.expectedImpact}</Text>}
            </View>

            {/* Risk Owner */}
            <View style={[styles.formGroup, styles.formGroupHalf]}>
              <Text style={styles.label}>
                {t('risks.riskOwner')}
                <Text style={styles.required}> *</Text>
              </Text>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: errors.riskOwner ? tokens.colors.raw.red["500"] : themeColors.inputBorder, color: themeColors.inputText }]}
                value={riskOwner}
                onChangeText={(val) => { setRiskOwner(val); clearFieldError('riskOwner'); }}
                placeholder={t('risks.riskOwnerPlaceholder')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
              {errors.riskOwner && <Text style={styles.errorText}>{errors.riskOwner}</Text>}
            </View>
          </View>

          {/* Current Applied Procedures */}
          <View style={styles.formGroup}>
            <Text style={styles.label}>
              {t('risks.currentProcedures')}
              <Text style={styles.required}> *</Text>
            </Text>
            <TextInput
              style={[styles.input, styles.textarea, { backgroundColor: themeColors.input, borderColor: errors.currentProcedures ? tokens.colors.raw.red["500"] : themeColors.inputBorder, color: themeColors.inputText }]}
              value={currentProcedures}
              onChangeText={(val) => { setCurrentProcedures(val); clearFieldError('currentProcedures'); }}
              placeholder=""
              placeholderTextColor={themeColors.placeholder}
              textAlign="right"
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
            {errors.currentProcedures && <Text style={styles.errorText}>{errors.currentProcedures}</Text>}
          </View>

          {/* Follow-up Actions */}
          <View style={styles.formGroup}>
            <Text style={styles.label}>
              {t('risks.mitigationActions')}
              <Text style={styles.required}> *</Text>
            </Text>
            <TextInput
              style={[styles.input, styles.textarea, { backgroundColor: themeColors.input, borderColor: errors.mitigationActions ? tokens.colors.raw.red["500"] : themeColors.inputBorder, color: themeColors.inputText }]}
              value={mitigationActions}
              onChangeText={(val) => { setMitigationActions(val); clearFieldError('mitigationActions'); }}
              placeholder=""
              placeholderTextColor={themeColors.placeholder}
              textAlign="right"
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
            {errors.mitigationActions && <Text style={styles.errorText}>{errors.mitigationActions}</Text>}
          </View>

          {/* Buttons */}
          <View style={styles.buttonRow}>
            <Button
              variant="primary"
              onPress={handleSave}
              loading={saving}
            >
              {t('common.save')}
            </Button>
            <Button
              variant="secondary"
              onPress={requestCancel}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            {isEditMode && (
              <Button
                variant="destructive"
                onPress={() => setShowDeleteConfirm(true)}
                loading={deleteRiskMutation.isPending}
                disabled={saving}
              >
                {t('common.delete')}
              </Button>
            )}
          </View>
        </View>
      </ScrollView>
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
      <ConfirmModal
        visible={showDeleteConfirm}
        title={t('risks.deleteRisk')}
        message={`${t('risks.deleteConfirm')}${name ? `\n${name}` : ''}`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#737373',
  },
  breadcrumbSeparator: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#D4D4D4',
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },
  // Title
  pageTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },
  // Form Card
  formCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["6"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },
  // Form Elements
  formRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  formGroup: {
    marginBottom: tokens.spacing.absolute["4"],
    position: 'relative',
    zIndex: 1,
  },
  formGroupHalf: {
    flex: 1,
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  required: {
    color: tokens.colors.raw.red["500"],
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },
  input: {
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    backgroundColor: tokens.colors.raw.white,
    textAlign: getTextAlign(isRTL),
  },
  textarea: {
    minHeight: 100,
    paddingTop: tokens.spacing.absolute["3"],
  },
  // Dropdown
  dropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    backgroundColor: tokens.colors.raw.white,
  },
  dropdownText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    marginTop: tokens.spacing.absolute["1"],
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 10,
    zIndex: 1000,
    maxHeight: 200,
    overflow: 'hidden',
  },
  dropdownScroll: {
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
    backgroundColor: tokens.colors.raw.white,
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  dropdownItemTextActive: {
    color: COLORS.primary,
    fontFamily: FONTS.medium,
  },
  // Buttons
  buttonRow: {
    flexDirection: 'row',
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["6"],
  },
});

const styles = getStyles(true);
